import { MetricUnit } from './types'

// Recharts targets this many ticks by default; we mirror it to estimate step size.
const TICK_COUNT = 5

export function makeYAxisTickFormatter(
  domain: [number, number | string],
  unit?: MetricUnit,
): (value: number) => string {
  const [min, max] = domain
  // Recharts uses 'auto' when it controls the domain — fall back to the simple formatter.
  if (typeof max === 'string' || typeof min === 'string')
    return (v) => formatYAxisTick(v, unit)

  // Percentage axes are typically tight (single digits), so a fixed 1-decimal
  // format reads cleaner than the count-oriented step heuristics below and
  // matches the headline value the user sees in the summary panel.
  if (unit === MetricUnit.Percentage) {
    const range = max - min
    const step = range / (TICK_COUNT - 1)
    const decimals = step >= 1 ? 1 : 2
    return (v) => `${v.toFixed(decimals)}%`
  }

  // Estimate the gap between adjacent ticks so we can choose how many decimals to show.
  // More decimals = more distinct labels for tight ranges; fewer = cleaner labels for wide ones.
  const range = max - min
  const step = range / (TICK_COUNT - 1)

  // Sub-1K values: show as plain numbers (e.g. 500, 999).
  if (max < 1_000) return (v) => String(v)

  // Million-range: use M suffix with precision scaled to the tick step.
  // e.g. wide range → "2M", medium → "1.5M", tight → "1.205M"
  if (max >= 1_000_000) {
    const decimals =
      step >= 1_000_000 ? 0 : step >= 100_000 ? 1 : step >= 10_000 ? 2 : 3
    return (v) => `${(v / 1_000_000).toFixed(decimals)}M`
  }

  // Thousand-range: use K suffix with precision scaled to the tick step.
  // e.g. wide range → "50K", medium → "2.9K", tight → "2.69K", very tight → "1.055K"
  if (step >= 1_000) return (v) => `${Math.round(v / 1_000)}K`
  if (step >= 100) return (v) => `${(v / 1_000).toFixed(1)}K`
  if (step >= 5) return (v) => `${(v / 1_000).toFixed(2)}K`
  return (v) => `${(v / 1_000).toFixed(3)}K`
}

export function formatYAxisTick(value: number, unit?: MetricUnit): string {
  if (unit === MetricUnit.Percentage) return `${value.toFixed(1)}%`
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000)
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return String(value)
}
